import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Cog8ToothIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { ROUTES } from '../../consts/general';
import { LOW_STOCK } from '../../consts/inventoryFilter';
import { AMAZON_FBA_LOW_STOCK_ALERTS } from '../../consts/reports';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';

const REPORT_KEY = AMAZON_FBA_LOW_STOCK_ALERTS;
const NUMERIC = new Set(['fbaSellable', 'fbaFcTransfer', 'fbaInbound', 'fbaDailyAvg', 'fbaDays', 'fbaReorderDays', 'fbaSuggestedSend', 'fbaVegasAvailable']);

const compareRows = (key, direction) => (a, b) => {
  const sign = direction === 'asc' ? 1 : -1;
  if (NUMERIC.has(key)) return sign * ((Number(a[key]) || 0) - (Number(b[key]) || 0));
  return sign * String(a[key] ?? '').localeCompare(String(b[key] ?? ''));
};

const AmazonFbaLowStockAlerts = ({ marginFromSidebar }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ field: 'fbaDays', direction: 'asc' });
  const [report, setReport] = useState({ rows: [], columns: [], sync: null });
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [inventoryError, setInventoryError] = useState(null);

  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings[REPORT_KEY] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory[REPORT_KEY] || [];

  const loadReport = useCallback(async () => {
    setInventoryLoading(true);
    setInventoryError(null);
    try {
      const res = await axios.get(`/api/v1/reports/${REPORT_KEY}`);
      setReport({ rows: res.data?.rows || [], columns: res.data?.columns || [], sync: res.data?.sync || null });
    } catch (error) {
      setReport({ rows: [], columns: [], sync: error?.response?.data?.sync || null });
      setInventoryError(error?.response?.data?.error || error.message);
    } finally {
      setInventoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
    dispatch(getReportEmailSettings(REPORT_KEY));
    dispatch(getReportSendHistory(REPORT_KEY));
  }, [dispatch, loadReport]);

  const syncState = report.sync?.state || null;
  const reportColumns = report.columns;

  const data = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = term
      ? report.rows.filter((r) => `${r.sku} ${r.productLine}`.toLowerCase().includes(term))
      : report.rows;
    return [...rows].sort(compareRows(sort.field, sort.direction));
  }, [report.rows, search, sort]);

  const columns = useMemo(() => reportColumns.map((c) => ({ key: c.key, label: c.label, sortable: true })), [reportColumns]);

  const renderRows = () =>
    data.map((row) => (
      <tr key={row.sku} className="hover:bg-gray-50">
        {reportColumns.map((c) => (
          <td key={c.key} className="px-4 py-2 text-sm text-gray-900">{row[c.key]}</td>
        ))}
      </tr>
    ));

  const handleBackToReports = () => navigate(ROUTES.REPORTS);
  const handleSort = ({ field, direction }) => setSort({ field, direction });

  const stockLevelsHref = useMemo(() => {
    const params = new URLSearchParams({ channel: 'amazon_fba', activeFilter: LOW_STOCK, sortBy: 'fba_days', sortDirection: 'ASC' });
    return `${ROUTES.SHOPIFY_AMAZON_INVENTORY}?${params.toString()}`;
  }, []);

  const syncLabel = syncState === 'FRESH' ? 'Fresh' : syncState === 'STALE' ? 'Stale' : syncState || 'Not loaded';

  const filtersComponent = (
    <div className="contents">
      <a className="text-sm text-blue-600 hover:underline" href={stockLevelsHref} target="_blank" rel="noopener noreferrer">
        Stock Levels: Amazon FBA Link
      </a>
      <span
        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${syncState === 'FRESH' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
      >
        Amazon FBA sync: {syncLabel}
      </span>
      <div className="flex flex-row items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search SKU / Product"
          className="w-40 sm:w-[220px] rounded-md border border-gray-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>
      <div className="flex items-center gap-3">
        <Cog8ToothIcon className="h-5 w-5 text-primary cursor-pointer" onClick={() => setIsOpenSettings(true)} />
      </div>
    </div>
  );

  const exportButton = <CSVExportButton data={data} columns={reportColumns} filename={REPORT_KEY} />;

  const handleSuccessSubmit = async (formData) => {
    const payload = {
      emailRecipients: formData?.emailRecipients || [],
      scheduleType: formData?.schedule || null,
      dayOfMonth: formData?.dayOfMonth ?? null,
      slackChannelId: formData?.slackChannelId ?? null,
      dayOfWeek: formData?.dayOfWeek ?? null,
      timeOfDay: formData?.timeOfDay ?? null,
      emailMessage: formData?.emailMessage ?? null,
      emailSendPreviewTo: formData?.emailSendPreviewTo ?? null,
    };

    try {
      await dispatch(updateReportEmailSettings({ reportType: REPORT_KEY, ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType,
        cronInterval,
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (formData) => {
    try {
      const payload = {
        email: formData?.emailSendPreviewTo,
        emailMessage: formData?.emailMessage || '',
        scheduleType: formData?.type || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: REPORT_KEY, ...payload })).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
      dispatch(getReportSendHistory(REPORT_KEY));
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview email', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (formData) => {
    try {
      const payload = {
        slackChannelId: formData?.slackChannelId,
        emailMessage: formData?.emailMessage || '',
        scheduleType: formData?.type || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: REPORT_KEY, ...payload })).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
      dispatch(getReportSendHistory(REPORT_KEY));
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' },
  ];

  const error = inventoryError ? String(inventoryError) : null;

  return (
    <>
      <ReportTemplate
        reportId={AMAZON_FBA_LOW_STOCK_ALERTS}
        description="Amazon FBA SKUs whose Amazon stock covers fewer days than lead time plus safety. Amazon-only net sales, trailing 90 days."
        breadcrumbs={breadcrumbs}
        marginFromSidebar={marginFromSidebar}
        onBack={handleBackToReports}
        isLoading={inventoryLoading}
        error={error}
        filters={filtersComponent}
        exportButton={exportButton}
        data={data}
        columns={columns}
        pagination={null}
        emptyMessage={error ? "Low stock can't be judged until the Amazon FBA sync is Fresh." : 'No Amazon FBA SKUs are low right now.'}
        enableSorting={true}
        sortField={sort.field}
        cronType={emailSettings?.cronType || null}
        sortDirection={sort.direction}
        onSort={handleSort}
      >
        {renderRows()}
      </ReportTemplate>

      <ReportEmailSettingsSidebar
        reportType={REPORT_KEY}
        isOpen={isOpenSettings}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        marginFromSidebar={marginFromSidebar}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix={REPORT_KEY}
      />
    </>
  );
};

export default AmazonFbaLowStockAlerts;

